// Logica pura delle gesture sull’anteprima: decide l’azione, nessun DOM.

import { getElementByCell, canAddElement } from './builderState';

export const cellClickAction = (state, cell) => {
  if (state.selection) {
    return 'toggle';
  }
  if (getElementByCell(state, cell)) {
    return 'edit';
  }
  if (canAddElement(state)) {
    return 'start';
  }
  return 'reject';
};

const EXTENT_THRESHOLDS = [
  { max: 0.295, key: 'quarter' },
  { max: 0.42, key: 'third' },
  { max: 0.585, key: 'half' },
  { max: 0.71, key: 'two-thirds' },
];

export const snapExtentKey = (fraction) => {
  for (const threshold of EXTENT_THRESHOLDS) {
    if (fraction < threshold.max) {
      return threshold.key;
    }
  }
  return 'three-quarters';
};

export const bandFractionFromPointer = ({
  placementKey,
  rect,
  clientX,
  clientY,
}) => {
  let fraction;
  if (placementKey === 'left') {
    fraction = (clientX - rect.left) / rect.width;
  } else if (placementKey === 'right') {
    fraction = (rect.right - clientX) / rect.width;
  } else if (placementKey === 'top') {
    fraction = (clientY - rect.top) / rect.height;
  } else {
    fraction = (rect.bottom - clientY) / rect.height;
  }
  return Math.min(1, Math.max(0, fraction));
};

// Sfumatura come manipolazione diretta: origine del colore e vettore
// dell’intero percorso (in coordinate 0..1 del foglietto) per direzione.
const GRADIENT_ORIGINS = {
  top: { x: 0.5, y: 0 },
  bottom: { x: 0.5, y: 1 },
  left: { x: 0, y: 0.5 },
  right: { x: 1, y: 0.5 },
  'top-left': { x: 0, y: 0 },
  'top-right': { x: 1, y: 0 },
  'bottom-left': { x: 0, y: 1 },
  'bottom-right': { x: 1, y: 1 },
};

const GRADIENT_VECTORS = {
  top: { x: 0, y: 1 },
  bottom: { x: 0, y: -1 },
  left: { x: 1, y: 0 },
  right: { x: -1, y: 0 },
  'top-left': { x: 1, y: 1 },
  'top-right': { x: -1, y: 1 },
  'bottom-left': { x: 1, y: -1 },
  'bottom-right': { x: -1, y: -1 },
};

const clamp01 = (value) => Math.min(1, Math.max(0, value));

// Frazione 0..1 del percorso della sfumatura sotto il puntatore; per il
// radiale ('edges') è la distanza dal centro (1 = metà lato).
export const gradientFractionFromPointer = ({
  directionKey,
  rect,
  clientX,
  clientY,
}) => {
  const px = (clientX - rect.left) / rect.width;
  const py = (clientY - rect.top) / rect.height;
  if (directionKey === 'edges') {
    return clamp01(Math.hypot(px - 0.5, py - 0.5) / 0.5);
  }
  const origin = GRADIENT_ORIGINS[directionKey];
  const vector = GRADIENT_VECTORS[directionKey];
  if (!origin || !vector) {
    return 0;
  }
  const lengthSq = vector.x * vector.x + vector.y * vector.y;
  const t =
    ((px - origin.x) * vector.x + (py - origin.y) * vector.y) / lengthSq;
  return clamp01(t);
};

// Snap dell’ampiezza: per il radiale la maniglia segna il confine del
// bianco centrale (vicino al centro = colore ampio), per le direzioni
// lineari segna dove il colore si esaurisce (lontano = ampio).
export const snapSpreadKey = (fraction, directionKey) => {
  if (directionKey === 'edges') {
    if (fraction < 0.18) return 'full';
    if (fraction < 0.33) return 'wide';
    if (fraction < 0.5) return 'medium';
    if (fraction < 0.66) return 'narrow';
    return 'minimal';
  }
  if (fraction < 0.225) return 'minimal';
  if (fraction < 0.41) return 'narrow';
  if (fraction < 0.635) return 'medium';
  if (fraction < 0.85) return 'wide';
  return 'full';
};

// I riposi lineari cadono al centro della fascia di snap corrispondente, così
// la maniglia non finisce mai in una zona che la rileggerebbe diversamente.
const LINEAR_HANDLE_FRACTIONS = {
  minimal: 0.15,
  narrow: 0.3,
  medium: 0.52,
  wide: 0.75,
  full: 0.95,
};
const RADIAL_HANDLE_FRACTIONS = {
  minimal: 0.74,
  narrow: 0.58,
  medium: 0.42,
  wide: 0.24,
  full: 0.12,
};

// Posizione percentuale della maniglia di ampiezza sul foglietto.
export const gradientHandlePosition = (directionKey, spreadKey) => {
  const clampPct = (value) => Math.min(96, Math.max(4, value * 100));
  if (directionKey === 'edges') {
    const fraction =
      RADIAL_HANDLE_FRACTIONS[spreadKey] || RADIAL_HANDLE_FRACTIONS.medium;
    return { left: clampPct(0.5 + fraction * 0.5), top: 50 };
  }
  const origin = GRADIENT_ORIGINS[directionKey];
  const vector = GRADIENT_VECTORS[directionKey];
  const fraction =
    LINEAR_HANDLE_FRACTIONS[spreadKey] || LINEAR_HANDLE_FRACTIONS.medium;
  return {
    left: clampPct(origin.x + vector.x * fraction),
    top: clampPct(origin.y + vector.y * fraction),
  };
};
